import { useEffect, useState } from "react";
import { FaBars, FaPlus, FaHome } from "react-icons/fa"; // Add FaHome
import { MdDragIndicator } from "react-icons/md";
import styles from "./Sidebar.module.css";
import api from "../../services/api";
import { ENDPOINTS } from "../../utils/endpoints";
import us30Image from "../../assets/images/us30.png";
import useAuthStore from "../../store/authStore";
import useDashboardStore from "../../store/dashboardStore";
import chatStore from "../../store/chatStore";
import BadgeCount from "../ChatPanel/BadgeCount";

// Import DND Kit
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SortableCommunityItem = ({
  community,
  index,
  active,
  onClick,
  unreadCount,
  isReorderMode,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: community.id,
    disabled: !isReorderMode,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isReorderMode ? "grab" : "pointer",
  };

  return (
    <div ref={setNodeRef} style={style} className={styles.badgeWrapper}>
      <div
        className={`${styles.sidebarItem} ${active ? styles.active : ""}`}
        onClick={!isReorderMode ? onClick : undefined}
        {...(isReorderMode ? { ...attributes, ...listeners } : {})}
      >
        <div className={styles.indicator} />
        <img
          src={`${ENDPOINTS.IMAGE_BASE_URL}${community.icon}`}
          alt="icon"
          className={styles.iconImage}
          draggable={false}
        />
        {isReorderMode && (
          <div className={styles.dragHandle}>
            <MdDragIndicator />
          </div>
        )}
      </div>
      {unreadCount > 0 && !isReorderMode && (
        <div className={styles.badge}>
          <BadgeCount count={unreadCount} />
        </div>
      )}
    </div>
  );
};

const SidebarItem = ({ active, onClick, children, isImage }) => (
  <div
    className={`${styles.sidebarItem} ${active ? styles.active : ""}`}
    onClick={onClick}
  >
    <div className={styles.indicator} />
    {isImage ? (
      <img src={children} alt="icon" className={styles.iconImage} />
    ) : (
      <div className={styles.iconBtn}>{children}</div>
    )}
  </div>
);

const Sidebar = () => {
  const { user } = useAuthStore();
  const { unreadChatCounts } = chatStore();
  const {
    isHomeScreen,
    isIconChoosed,
    isUS30Choosed,
    communityIndex,
    communities,
    setIsHomeScreen,
    setIsIconChoosed,
    setIsUS30Choosed,
    setCommunityIndex,
    setCommunities,
    allChannels,
    setAllChannels,
  } = useDashboardStore();

  const [debouncedCounts, setDebouncedCounts] = useState({});
  const [isReorderMode, setIsReorderMode] = useState(false);

  // DND Kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    const fetchCommunities = async () => {
      try {
        const { data } = await api.get(ENDPOINTS.GET_ALL_COMMUNITIES);
        if (data.success) {
          setCommunities(data.communities);

          setTimeout(() => {
            const lastScreen = localStorage.getItem("lastScreen");
            if (
              lastScreen === "ChatScreen" ||
              lastScreen === "CommunityContentScreen" ||
              lastScreen === "UpcomingLiveSessionScreen"
            ) {
              const communityIndex = parseInt(
                localStorage.getItem("communityIndex"),
                10
              );

              if (!isNaN(communityIndex)) {
                setCommunityIndex(communityIndex);
              }
            }
          }, 500);
        }
      } catch (err) {
        console.error("Failed to fetch communities", err);
      }
    };

    const fetchAllChannels = async () => {
      try {
        const { data } = await api.get(ENDPOINTS.GET_ALL_CHANNELS);
        if (data.success) {
          setAllChannels(data.channels);
        }
      } catch (err) {
        console.error("Failed to fetch all channels", err);
      }
    };

    fetchCommunities();
    fetchAllChannels();
  }, [setCommunities, setAllChannels]);

  // Debounce unread counts
  useEffect(() => {
    const handler = setTimeout(() => {
      const grouped = allChannels.reduce((acc, channel) => {
        const communityName = channel.community;
        const count = unreadChatCounts[channel.id] || 0;
        if (!acc[communityName]) acc[communityName] = 0;
        acc[communityName] += count;
        return acc;
      }, {});
      setDebouncedCounts(grouped);
    }, 300);

    return () => clearTimeout(handler);
  }, [unreadChatCounts, allChannels]);

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = communities.findIndex((c) => c.id === active.id);
    const newIndex = communities.findIndex((c) => c.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const reorderedCommunities = arrayMove(communities, oldIndex, newIndex);
    setCommunities(reorderedCommunities);

    try {
      const orders = reorderedCommunities.map((item, index) => ({
        id: item.id,
        sort_order: index,
      }));

      await api.post(ENDPOINTS.REORDER_COMMUNITIES, { orders });
    } catch (err) {
      console.error("Failed to reorder communities", err);
      const { data } = await api.get(ENDPOINTS.GET_ALL_COMMUNITIES);
      if (data.success) {
        setCommunities(data.communities);
      }
    }
  };

  return (
    <div className={styles.sidebar}>
      {/* Home button - First item */}
      <SidebarItem
        active={isHomeScreen}
        onClick={() => {
          localStorage.removeItem("lastScreen");
          setIsHomeScreen();
        }}
      >
        <FaHome />
      </SidebarItem>

      {/* Admin/Trader menu icon */}
      {user?.roles[0] === "administrator" && (
        <>
          <SidebarItem
            active={isIconChoosed}
            onClick={() => {
              localStorage.removeItem("lastScreen");
              setIsIconChoosed();
            }}
          >
            <FaBars />
          </SidebarItem>

          {/* Reorder toggle button - Admin only */}
          <button
            className={styles.reorderButton}
            onClick={() => setIsReorderMode(!isReorderMode)}
            title={isReorderMode ? "Done reordering" : "Reorder communities"}
          >
            {isReorderMode ? "✓" : "⋮⋮"}
          </button>
        </>
      )}

      {user?.roles[0] === "trader" && (
        <SidebarItem
          active={isIconChoosed}
          onClick={() => {
            localStorage.removeItem("lastScreen");
            setIsIconChoosed();
          }}
        >
          <FaPlus />
        </SidebarItem>
      )}

      {/* Communities with drag and drop */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={communities.map((c) => c.id)}
          strategy={verticalListSortingStrategy}
          disabled={!isReorderMode}
        >
          {communities.map((community, index) => (
            <SortableCommunityItem
              key={community.id}
              community={community}
              index={index}
              active={communityIndex === index}
              onClick={() => {
                localStorage.removeItem("lastScreen");
                setCommunityIndex(index);
              }}
              unreadCount={debouncedCounts[community.name] || 0}
              isReorderMode={isReorderMode}
            />
          ))}
        </SortableContext>
      </DndContext>

      {/* US30 tab */}
      <SidebarItem active={isUS30Choosed} onClick={setIsUS30Choosed} isImage>
        {us30Image}
      </SidebarItem>
    </div>
  );
};

export default Sidebar;
